import React, { useEffect, useState } from "react";
import {
  ProcessAction,
  ProcessCompletedStep,
  ProcessData,
  ProcessStep,
} from "./GuidedProcess.types";
import SelectedStep from "./components/SelectedStep";
import ActiveStep from "./components/ActiveStep";
import Glossary from "../functions/glossary";
import MemoHandIcon from "../Components/HandIcon";
import { AnimatePresence, motion } from "framer-motion";

const GuidedProcessTool: React.FC<ProcessData> = ({
  steps,
  alertContent,
  title,
  introContent,
  backLink,
  processMapLink,
  finalStepContent,
}) => {
  const [started, setStarted] = useState(false);
  const [activeStep, setActiveStep] = useState<ProcessStep | null>(
    steps[0] ?? null
  );
  const [completedSteps, setCompletedSteps] = useState<ProcessCompletedStep[]>(
    []
  );

  const handleNextStep = (nextStepIndex: number, nextStepId: number) => {
    // do filtering for next step with id and then set active step to to the next step
    const nextStep = steps.find((step) => step.id === nextStepId);

    if (activeStep && activeStep.nextSteps) {
      const selectedAction = activeStep.nextSteps[nextStepIndex];

      if (selectedAction) {
        // add the selected step to the array of selected steps
        setCompletedSteps([
          ...completedSteps,
          {
            step: activeStep,
            action: selectedAction,
          },
        ]);
      }
    }

    if (nextStep) {
      setActiveStep(nextStep);
    } else {
      setActiveStep(null);
    }
  };

  const isLastStep =
    !activeStep || !activeStep.nextSteps || activeStep.nextSteps.length === 0;

  useEffect(() => {
    setTimeout(() => {
      Glossary();
    }, 100);
  }, [activeStep, started]);
  return (
    <div>
      {started && <p className="mobile-title">{title}</p>}
      <div className="guided-process">
        {!started && (
          <div className="guided-process__intro">
            <div className="guided-process__intro--image">
              <MemoHandIcon /> Guide Me
            </div>

            <h2 className="h3">{title}</h2>

            {introContent && (
              <div
                className="mjb--copy"
                data-glossary
                dangerouslySetInnerHTML={{ __html: introContent }}
              ></div>
            )}

            {alertContent && (
              <div
                className="block-alert block-alert--info rte"
                data-glossary-ignore
              >
                <div dangerouslySetInnerHTML={{ __html: alertContent }}></div>
              </div>
            )}

            <div className="guided-process__intro--buttons">
              {backLink && (
                <a
                  href={backLink}
                  className="guided-process__button guided-process__button--outline"
                >
                  Back
                </a>
              )}
              <button
                className="guided-process__button guided-process__button--fill"
                onClick={() => {
                  setStarted(true);
                  setActiveStep(steps[0]);
                  setCompletedSteps([]);
                }}
              >
                Begin journey
              </button>
            </div>
          </div>
        )}

        <div>
          {started && (
            <div>
              <div className="guided-process__app-container">
                {completedSteps &&
                  completedSteps.map((completedStep, index) => (
                    <SelectedStep
                      step={completedStep.step}
                      action={completedStep.action}
                      key={index}
                      index={index}
                      updateSelectedAction={(action: ProcessAction) => {
                        const newCompletedSteps = completedSteps.slice(
                          0,
                          index
                        );
                        newCompletedSteps.push({
                          step: completedStep.step,
                          action: action,
                        });

                        setCompletedSteps(newCompletedSteps);
                        setActiveStep(
                          steps.find((step) => step.id === action.nextStepId)
                        );
                      }}
                    />
                  ))}

                <AnimatePresence mode="wait">
                  {activeStep && (
                    <motion.div
                      variants={{
                        hidden: { opacity: 0 },
                        visible: { opacity: 1 },
                      }}
                      initial="hidden"
                      animate="visible"
                      exit="hidden"
                    >
                      <ActiveStep
                        step={activeStep}
                        handleNextStep={handleNextStep}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>

                {isLastStep ? (
                  <div>
                    {finalStepContent ? (
                      <div
                        className="guided-process__final-step-content"
                        data-glossary
                        dangerouslySetInnerHTML={{
                          __html: finalStepContent,
                        }}
                      />
                    ) : (
                      <div>
                        <p>
                          <strong>
                            This is the final step along your chosen pathway.
                          </strong>
                        </p>
                        <p>
                          To view another path, scroll up and change any of your
                          previous selections, or press the Restart button below
                          to start again. You can also view all pathways on the
                          interactive process map.
                        </p>
                      </div>
                    )}

                    <div className="guided-process__footer">
                      <button
                        className="guided-process__button guided-process__button--outline"
                        onClick={() => {
                          setStarted(false);
                          setActiveStep(steps[0]);
                          setCompletedSteps([]);
                          const banner = document.querySelector(".moj-banner");
                          if (banner) {
                            banner.scrollIntoView(true);
                          }
                        }}
                      >
                        Restart
                      </button>
                      {processMapLink && (
                        <a
                          href={processMapLink}
                          className="guided-process__button guided-process__button--fill"
                        >
                          See the map
                        </a>
                      )}
                    </div>
                  </div>
                ) : (
                  <button
                    className="guided-process__button guided-process__button--outline guided-process__button--restart"
                    onClick={() => {
                      setStarted(false);
                      setActiveStep(steps[0]);
                      setCompletedSteps([]);
                      const banner = document.querySelector(".moj-banner");
                      if (banner) {
                        banner.scrollIntoView(true);
                      }
                    }}
                  >
                    Restart
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuidedProcessTool;
